export const PhotoQualityThresholds = {
    minLongSide: 800,
    minShortSide: 600,
    minColorfulness: 15,
    maxBlackPixelPercentage: 50,
    blackPixelChannelThreshold: 30,
    blacknessRegionSize: 12
};

export const PhotoQualityMessages = {
    lowResolution: 'The photo resolution is too low. Please upload a photo with at least 800x800 resolution.',
    blackAndWhite: 'The photo appears to be black and white. Please upload a color photo.',
    blackBackground: 'The photo background is too black. Please upload a photo with a lighter background.'
};

export interface IPhotoQualityResult {
    isValid: boolean;
    errorMessage?: string;
}

export function isPureBlackPixel(r: number, g: number, b: number): boolean {
    return r === 0 && g === 0 && b === 0;
}

export function isBlackPixel(r: number, g: number, b: number, threshold = PhotoQualityThresholds.blackPixelChannelThreshold): boolean {
    return r < threshold && g < threshold && b < threshold;
}

export function calculateBrightness(r: number, g: number, b: number): number {
    return (r * 299 + g * 587 + b * 114) / 1000;
}

/** Percentage of black pixels sampled from eight regions around the image border. */
export function sampleBlackness(img: HTMLImageElement): number[] {
    const blackSamples: number[] = [];
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const regionSize = PhotoQualityThresholds.blacknessRegionSize;
    const halfRegion = Math.floor(regionSize / 2);

    const samplePositions = [
        { x: 0, y: 0 },
        { x: w - 1, y: 0 },
        { x: 0, y: h - 1 },
        { x: w - 1, y: h - 1 },
        { x: Math.floor(w / 2), y: 0 },
        { x: Math.floor(w / 2), y: h - 1 },
        { x: 0, y: Math.floor(h / 2) },
        { x: w - 1, y: Math.floor(h / 2) }
    ];

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { return []; }
    ctx.drawImage(img, 0, 0, w, h);

    samplePositions.forEach(pos => {
        let blackCount = 0;
        let count = 0;
        for (let dx = -halfRegion; dx < regionSize - halfRegion; dx++) {
            for (let dy = -halfRegion; dy < regionSize - halfRegion; dy++) {
                const x = Math.min(Math.max(pos.x + dx, 0), w - 1);
                const y = Math.min(Math.max(pos.y + dy, 0), h - 1);
                const pixel = ctx.getImageData(x, y, 1, 1).data;
                if (isBlackPixel(pixel[0], pixel[1], pixel[2])) {
                    blackCount++;
                }
                count++;
            }
        }
        blackSamples.push((blackCount / count) * 100);
    });

    return blackSamples;
}

/** Hasler & Suesstrunk colourfulness metric; low values indicate a greyscale photo. */
export function checkColorfulness(img: HTMLImageElement): number {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) { return 0; }
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

    const imageData = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight).data;
    const rg = [];
    const yb = [];
    for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i], g = imageData[i + 1], b = imageData[i + 2];
        rg.push(Math.abs(r - g));
        yb.push(Math.abs(0.5 * (r + g) - b));
    }
    const meanRG = rg.reduce((a, b) => a + b, 0) / rg.length;
    const meanYB = yb.reduce((a, b) => a + b, 0) / yb.length;
    const stdRG = Math.sqrt(rg.reduce((a, b) => a + Math.pow(b - meanRG, 2), 0) / rg.length);
    const stdYB = Math.sqrt(yb.reduce((a, b) => a + Math.pow(b - meanYB, 2), 0) / yb.length);
    return Math.sqrt(stdRG ** 2 + stdYB ** 2) + 0.3 * Math.sqrt(meanRG ** 2 + meanYB ** 2);
}

export function hasAcceptableResolution(img: HTMLImageElement): boolean {
    const { minLongSide, minShortSide } = PhotoQualityThresholds;
    return (img.naturalWidth >= minLongSide && img.naturalHeight >= minShortSide) ||
        (img.naturalHeight >= minLongSide && img.naturalWidth >= minShortSide);
}

/** Runs resolution, colourfulness and background-blackness checks in that order. */
export function validatePhotoQuality(img: HTMLImageElement): IPhotoQualityResult {
    if (!hasAcceptableResolution(img)) {
        return { isValid: false, errorMessage: PhotoQualityMessages.lowResolution };
    }

    if (checkColorfulness(img) < PhotoQualityThresholds.minColorfulness) {
        return { isValid: false, errorMessage: PhotoQualityMessages.blackAndWhite };
    }

    const blackSamples = sampleBlackness(img);
    const blackPixelPercentage = blackSamples.reduce((a, b) => a + b, 0) / blackSamples.length;
    if (blackPixelPercentage > PhotoQualityThresholds.maxBlackPixelPercentage) {
        return { isValid: false, errorMessage: PhotoQualityMessages.blackBackground };
    }

    return { isValid: true };
}
